import React, { useMemo, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { type TooltipItem } from 'chart.js';
import { type Context as DataLabelContext } from 'chartjs-plugin-datalabels';
import { Chart } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';

const BAR_BLUE = 'rgb(47, 85, 151)';
const LINE_SLATE = 'rgb(71, 85, 105)';

export interface AssigneeCloseRow {
  userId: string;
  displayName: string;
  count: number;
  avgClosedDays: number;
}

export interface KpiAssigneeAvgCloseChartProps {
  rows: AssigneeCloseRow[];
  panelTitle?: string;
  emptyMessage?: string;
  /** Right-axis line series label (e.g. "Avg closed days" vs "Avg days open"). */
  lineDatasetLabel?: string;
  selectedUserId?: string | null;
  selectedBarColor?: string;
  onAssigneeClick?: (userId: string) => void;
  /** Click on the bar's count label to open ticket table. */
  onAssigneeCountClick?: (userId: string) => void;
}

const DEFAULT_TITLE = 'Tickets Assignee w/ Avg Closed Days';

const KpiAssigneeAvgCloseChart: React.FC<KpiAssigneeAvgCloseChartProps> = ({
  rows,
  panelTitle = DEFAULT_TITLE,
  emptyMessage = 'No closed Incident or Request tickets in this period for the selected filters.',
  lineDatasetLabel = 'Avg closed days',
  selectedUserId = null,
  selectedBarColor = 'rgb(30, 58, 120)',
  onAssigneeClick,
  onAssigneeCountClick,
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const labels = rows.map((r) => r.displayName);
  const counts = rows.map((r) => r.count);
  const avgDays = rows.map((r) => r.avgClosedDays);
  const barColors = useMemo(
    () =>
      rows.map((r) => (selectedUserId != null && r.userId === selectedUserId ? selectedBarColor : BAR_BLUE)),
    [rows, selectedUserId, selectedBarColor]
  );
  const y1Max = useMemo(() => {
    const m = Math.max(0, ...avgDays, 1);
    const padded = m * 1.12;
    return Math.ceil(padded);
  }, [avgDays]);

  const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onAssigneeClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null || el.datasetIndex !== 0) return;
    const row = rows[el.index];
    if (!row || row.count <= 0) return;
    onAssigneeClick(row.userId);
  };

  if (!rows.length) {
    return (
      <KpiChartPanel title={panelTitle} className="h-full lg:col-span-1" flexFill>
        <p className="px-4 py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel title={panelTitle} className="h-full lg:col-span-1" flexFill>
      <div className="relative min-h-[400px] w-full flex-1">
        <Chart
          ref={chartRef}
          type="bar"
          onClick={onAssigneeClick ? handleChartClick : undefined}
          data={{
            labels,
            datasets: [
              {
                label: 'Ticket count',
                data: counts,
                backgroundColor: barColors,
                borderRadius: 6,
                barThickness: 28,
                yAxisID: 'y',
                order: 1,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  color: '#111827',
                  font: { weight: 600, size: 10 },
                  formatter: (value: number) => formatDataLabelCompact(Number(value)),
                  listeners: onAssigneeCountClick
                    ? ({
                        click: (ctx: DataLabelContext, event: any) => {
                          const idx = ctx.dataIndex;
                          const row = rows[idx];
                          if (!row || row.count <= 0) return false;
                          onAssigneeCountClick(row.userId);
                          event?.native?.stopPropagation?.();
                          return true;
                        },
                      } as any)
                    : undefined,
                },
              },
              {
                label: lineDatasetLabel,
                data: avgDays,
                type: 'line',
                borderColor: LINE_SLATE,
                backgroundColor: 'transparent',
                borderWidth: 2,
                tension: 0.35,
                pointRadius: 4,
                pointHoverRadius: 5,
                pointBackgroundColor: '#ffffff',
                pointBorderColor: LINE_SLATE,
                pointBorderWidth: 2,
                fill: false,
                yAxisID: 'y1',
                order: 2,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 14,
                  color: '#475569',
                  font: { weight: 700, size: 9 },
                  formatter: (value: number, ctx: DataLabelContext) => {
                    const v = Number(ctx.dataset.data[ctx.dataIndex]);
                    return Number.isFinite(v) ? v.toFixed(1) : '0';
                  },
                },
              },
            ] as any,
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            onHover: (event, elements) => {
              const canvas = event.native?.target;
              if (!(canvas instanceof HTMLElement)) return;
              const el = elements[0];
              const idx = el?.index;
              const row = idx !== undefined ? rows[idx] : undefined;
              const onBar = el?.datasetIndex === 0;
              canvas.style.cursor =
                onAssigneeClick && row && onBar && row.count > 0 ? 'pointer' : 'default';
            },
            layout: { padding: { top: 28, right: 10, left: 4, bottom: 8 } },
            plugins: {
              legend: {
                display: true,
                position: 'bottom',
                align: 'center',
                labels: {
                  boxWidth: 18,
                  boxHeight: 10,
                  padding: 14,
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets;
                    const lineLabel = typeof ds[1]?.label === 'string' ? ds[1].label : lineDatasetLabel;
                    return [
                      {
                        text: 'Ticket count',
                        fillStyle: BAR_BLUE,
                        strokeStyle: 'transparent',
                        lineWidth: 0,
                        hidden: false,
                        index: 0,
                        datasetIndex: 0,
                      },
                      {
                        text: lineLabel,
                        fillStyle: '#ffffff',
                        strokeStyle: LINE_SLATE,
                        lineWidth: 1,
                        hidden: !ds[1],
                        index: 1,
                        datasetIndex: 1,
                      },
                    ] as any;
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (ctx: TooltipItem<'bar'>) => {
                    if (ctx.dataset.label === lineDatasetLabel) {
                      return `${ctx.dataset.label}: ${Number(ctx.raw).toFixed(1)}`;
                    }
                    return `${ctx.dataset.label}: ${ctx.raw}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: '#6b7280', font: { size: 10 }, maxRotation: 35 },
              },
              y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                border: { display: false },
                grid: { color: 'rgba(0,0,0,0.06)' },
                ticks: {
                  color: '#9ca3af',
                  maxTicksLimit: 8,
                  callback: (v) => formatAxisCompactK(v as number),
                },
              },
              y1: {
                type: 'linear',
                display: true,
                position: 'right',
                min: 0,
                max: y1Max,
                border: { display: false },
                grid: { drawOnChartArea: false },
                ticks: {
                  maxTicksLimit: 8,
                  color: '#9ca3af',
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiAssigneeAvgCloseChart;
